import Head from 'next/head'
import Image from 'next/image'

import Box from '@totallymoney/ui/components/Box'
import Flex from '@totallymoney/ui/components/Flex'
import Stack from '@totallymoney/ui/components/Stack'
import TrustBox from '@totallymoney/ui/components/TrustBox'
import theme from '@totallymoney/ui/theme'

import useAppQueryState from '@hooks/useAppQueryState'

import backgroundQuotationScaleLevel40 from '@images/background-quotation-scaleLevel40.png'
import backgroundBlobIndigo30 from '@images/background-smooth-blob-indigo30.png'
import backgroundRough04 from '@images/background-texture-rough-04.png'
import backgroundScoreLinesSemiCircleAqua10 from '@images/scorelines-semi-circle-aqua10.png'
import backgroundScoreLinesSemiCirclePurple40 from '@images/scorelines-semi-circle-purple40.png'

import AboutSection from './components/AboutSection'
import ApplySection from './components/ApplySection'
import ExpertSection from './components/ExpertSection'
import FaqsSection from './components/FaqsSection'
import FindingTheRightCardSection from './components/FindingTheRightCardSection'
import PageTypesSection from './components/PageTypesSection'
import ProsAndConsSection from './components/ProsAndConsSection'
import ProvidersSection from './components/ProvidersSection'
import RelatedArticleSection from './components/RelatedArticleSection'
import ReviewsSection from './components/ReviewsSection'
import SectionWrapper from './components/SectionWrapper'
import CategoryPageNavigation from './components/UnscoredCategoryPageNavigation'
import UnscoredHero from './components/UnscoredHero'
import WhyTotallyMoneySection from './components/WhyTotallyMoneySection'
import aboutTitles from './lib/aboutTitles'
import applyTitles from './lib/applyTitles'
import findTheRightCardData from './lib/findTheRightCardData'
import metaData from './lib/metaData'
import prosAndConsTitle from './lib/prosAndConsTitle'

const UnscoredCategoryPageTemplate = () => {
    const { appQueryState } = useAppQueryState()

    const category = appQueryState.categoryId!

    return (
        <>
            <Head>
                <title>{`${metaData[category].title} - TotallyMoney`}</title>

                <meta
                    name="description"
                    content={metaData[category].description}
                />

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'TotallyMoney',
                            url: 'https://totallymoney.com',
                            logo: 'https://www.totallymoney.com/favicon-32x32.png',
                            sameAs: [
                                'https://www.facebook.com/totallymoney',
                                'https://twitter.com/totallymoney',
                                'https://uk.linkedin.com/company/totallymoney',
                                'https://www.tiktok.com/@totallymoney',
                                'https://www.instagram.com/totallymoney/',
                                'https://www.youtube.com/channel/UCzT81m3neFD3QSqEwX0IAyg',
                            ],
                        }),
                    }}
                />
            </Head>

            <Box as="main">
                <UnscoredHero />

                <Box mb="space20">
                    <TrustBox />
                </Box>

                <CategoryPageNavigation />

                <Stack space={0}>
                    <SectionWrapper>
                        <ProvidersSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={`linear-gradient(180deg, ${theme.neutral80} 0%, transparent 100%)`}
                        hasWave
                        id="about-credit-cards"
                        title={aboutTitles[category]}
                        waveColor={theme.neutral80}
                    >
                        <AboutSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={`linear-gradient(180deg, ${theme.indigo10} 0%, transparent 100%)`}
                        title={applyTitles[category]}
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <ApplySection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={theme.indigo20}
                        backgroundTexture={
                            <Flex
                                justifyContent="center"
                                alignItems={['flex-start', 'center']}
                                zIndex={0}
                                mt="space130"
                                aria-hidden="true"
                            >
                                <Image
                                    src={backgroundBlobIndigo30}
                                    alt=""
                                    placeholder="empty"
                                    sizes="100vw"
                                    quality={60}
                                    priority={false}
                                />
                            </Flex>
                        }
                        hasWave
                        id="types-of-card"
                        supportingText="There are a number of different types of credit card on the market, such as:"
                        title="Types of credit card"
                        waveColor={theme.indigo20}
                    >
                        <PageTypesSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor="white"
                        backgroundTexture={
                            <>
                                <Box
                                    display={['none', 'none', 'flex']}
                                    alignItems="center"
                                    position="absolute"
                                    left={0}
                                    height="90%"
                                    aria-hidden="true"
                                >
                                    <Image
                                        src={
                                            backgroundScoreLinesSemiCircleAqua10
                                        }
                                        alt=""
                                        placeholder="empty"
                                        sizes="100vw"
                                        quality={60}
                                        priority={false}
                                    />
                                </Box>

                                <Box
                                    display={['none', 'none', 'flex']}
                                    alignItems="flex-end"
                                    position="absolute"
                                    right={0}
                                    height="90%"
                                >
                                    <Image
                                        src={
                                            backgroundScoreLinesSemiCirclePurple40
                                        }
                                        alt="Scorelines circle background texture"
                                        placeholder="empty"
                                        sizes="100vw"
                                        quality={60}
                                        priority={false}
                                    />
                                </Box>
                            </>
                        }
                        hasWave
                        id="pros-and-cons"
                        supportingText="There are advantages and disadvantages to having credit cards, such as:"
                        title={prosAndConsTitle[category]}
                        waveColor="white"
                    >
                        <ProsAndConsSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={theme.indigo10}
                        backgroundTexture={
                            <Flex
                                alignItems="flex-end"
                                position="absolute"
                                top={['160px', 'space60']}
                                right={['space30', 'space30', '5%', '10%']}
                                aria-hidden="true"
                            >
                                <Image
                                    src={backgroundQuotationScaleLevel40}
                                    alt=""
                                    placeholder="empty"
                                    sizes="100vw"
                                    quality={60}
                                    priority={false}
                                />
                            </Flex>
                        }
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <ExpertSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor="white"
                        hasWave
                        id="find-the-right-card"
                        title={findTheRightCardData[category].title}
                        supportingText={
                            findTheRightCardData[category].supportingText
                        }
                        waveColor="white"
                    >
                        <FindingTheRightCardSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={theme.indigo10}
                        hasWave
                        id="why-totallymoney"
                        title="Why use TotallyMoney"
                        supportingText="With TotallyMoney, you can compare credit cards from across the UK market and check your eligibility before making an application. This will help protect your credit score as you can then only apply for cards you’re likely to be accepted for. Too many rejections can have a negative impact on your credit score."
                        waveColor={theme.indigo10}
                    >
                        <WhyTotallyMoneySection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor="white"
                        hasWave
                        waveColor="white"
                    >
                        <FaqsSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={theme.indigo10}
                        hasWave
                        waveColor={theme.indigo10}
                    >
                        <RelatedArticleSection />
                    </SectionWrapper>

                    <SectionWrapper
                        backgroundColor={theme.indigo20}
                        backgroundTexture={
                            <Flex justifyContent="center" aria-hidden="true">
                                <Image
                                    src={backgroundRough04}
                                    alt=""
                                    placeholder="empty"
                                    sizes="100vw"
                                    quality={60}
                                    priority={false}
                                />
                            </Flex>
                        }
                        hasWave
                        title="See why our customers love us"
                        waveColor={theme.indigo20}
                    >
                        <ReviewsSection />
                    </SectionWrapper>
                </Stack>
            </Box>
        </>
    )
}

export default UnscoredCategoryPageTemplate
